// @flow
import * as React from 'react';
import {useState, useEffect, useMemo} from 'react';
import {contextStubFunction, DEFAULT_APPEARANCE_COLORS} from 'docs/util';
import {useAccount, useApi} from 'docs/data';

import type {Appearance} from 'docs/util';

type SetState<T> = (T | ((T) => T)) => void;
type Price = Object;

export type DataContextType = {|
  appearance: Appearance,
  setAppearance: SetState<Appearance>,
  activePrice: ?Price,
  setActivePrice: SetState<Price>,
  prices: Array<Price>,
  taxActivationStatus: ?string,
  setPrices: SetState<Array<Price>>,
  paymentMethods: Set<string>,
  setPaymentMethods: SetState<Set<string>>,
|};

export const DataContext = React.createContext<DataContextType>({
  appearance: {},
  setAppearance: contextStubFunction('DataProvider'),
  activePrice: null,
  setActivePrice: contextStubFunction('DataProvider'),
  taxActivationStatus: null,
  prices: [],
  setPrices: contextStubFunction('DataProvider'),
  paymentMethods: new Set([]),
  setPaymentMethods: contextStubFunction('DataProvider'),
});

type Props = {|
  children: React.Node,
|};

export const DataProvider = (props: Props) => {
  const [appearance, setAppearance] = useState({
    theme: 'stripe',
    ...DEFAULT_APPEARANCE_COLORS.stripe,
  });
  const [activePrice, setActivePrice] = useState(null);
  const [prices, setPrices] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState(new Set(['card']));
  const [taxActivationStatus, setTaxActivationStatus] = useState(null);
  const {account} = useAccount();
  const {api} = useApi();

  const isLoggedIn = account.merchant?.isLoggedIn;
  useEffect(() => {
    if (isLoggedIn) {
      api.getPrices().then((res) => {
        if (res?.data?.length) {
          setPrices(res.data);
        }
      });
      api.getTaxData().then((res) => {
        if (res?.activation_status) {
          setTaxActivationStatus(res.activation_status);
        }
      });
    }
  }, [api, isLoggedIn]);

  const value = useMemo(
    () => ({
      appearance,
      setAppearance,
      activePrice,
      setActivePrice,
      prices,
      setPrices,
      taxActivationStatus,
      paymentMethods,
      setPaymentMethods,
    }),
    [appearance, activePrice, prices, taxActivationStatus, paymentMethods],
  );

  return (
    <DataContext.Provider value={value}>{props.children}</DataContext.Provider>
  );
};

export const useData = (): DataContextType => {
  return React.useContext(DataContext);
};
